// Die eine IndexedDB dieser Anwendung, und wer darin wohnt.
//
// Vorher stand das Öffnen in drafts.ts, weil es dort nur einen Speicher gab.
// Seit der Gast-Trichter (`guestResult.ts`) einen zweiten braucht, ist es eine
// eigene Datei -- und das ist keine Kosmetik:
//
// **Eine Datenbank hat genau eine Version.** Öffnete guestResult.ts dieselbe
// Datenbank mit `2`, während drafts.ts weiter `1` verlangt, bekäme jeder
// Browser, der schon einmal aufgerüstet hat, beim nächsten Editor-Start einen
// `VersionError` -- und damit keinen Entwurf mehr. Die Versionszahl und das
// `onupgradeneeded`, das alle Speicher anlegt, stehen deshalb an genau einer
// Stelle.
//
// Beim Abmelden löscht `lib/serviceWorker.ts` die ganze Datenbank
// (`deleteDatabase`). Das gilt seit jeher für die Entwürfe und ab jetzt auch
// für ein Gast-Ergebnis -- richtig so: was auf einem fremden Rechner liegen
// bleibt, hat dort nichts verloren.

export const DATABASE_NAME = 'schnittklar'

// 1 -> 2: der Speicher `guest` kam dazu.
export const DATABASE_VERSION = 2

export const DRAFT_STORE = 'drafts'
export const GUEST_STORE = 'guest'

const STORES: ReadonlyArray<{ name: string; keyPath: string }> = [
  { name: DRAFT_STORE, keyPath: 'projectId' },
  { name: GUEST_STORE, keyPath: 'key' },
]

export function request<T>(target: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    target.onsuccess = () => resolve(target.result)
    target.onerror = () => reject(target.error)
  })
}

export function openDatabase(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const opening = factory.open(DATABASE_NAME, DATABASE_VERSION)
    opening.onupgradeneeded = () => {
      const database = opening.result
      for (const store of STORES) {
        if (!database.objectStoreNames.contains(store.name)) {
          database.createObjectStore(store.name, { keyPath: store.keyPath })
        }
      }
    }
    opening.onsuccess = () => resolve(opening.result)
    opening.onerror = () => reject(opening.error)
    opening.onblocked = () => reject(new Error('IndexedDB blocked'))
  })
}

/**
 * Eine Verbindung, die offen bleibt: einmal öffnen, dann wiederverwenden.
 * Ein Fehler beim Öffnen wird beim nächsten Zugriff noch einmal versucht,
 * statt für immer gemerkt zu werden.
 */
export function connection(factory: IDBFactory): () => Promise<IDBDatabase> {
  let database: Promise<IDBDatabase> | null = null

  return () => {
    if (database === null) {
      database = openDatabase(factory).catch((error) => {
        database = null
        throw error
      })
    }
    return database
  }
}
